import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import { FileCheck2, Gauge, BellRing, Check, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import { CtaTopScribble, CtaUnderlineScribble } from "@/components/site/VectorMarks";


type Region = { title: string; desc: string; pinColor: string; count: string };

const regions: Region[] = [
  { title: "North America", desc: "We connect directly to carriers across the US, Canada, and Mexico, with support for 10DLC and toll-free numbers.", pinColor: "text-teal-500", count: "" },
  { title: "Europe", desc: "Our EU and UK routes are GDPR-compliant, with alphanumeric sender ID support across every member state.", pinColor: "text-violet-500", count: "" },
  { title: "Asia Pacific", desc: "We cover India, Southeast Asia, Australia, and Japan, handling local compliance requirements in each market.", pinColor: "text-blue-500", count: "" },
  { title: "Latin America", desc: "We route directly into Brazil, Argentina, Colombia, and other key markets, with local sender ID support built in.", pinColor: "text-orange-500", count: "" },
  { title: "Middle East & Africa", desc: "We support sender ID registration and premium routing across the UAE, Saudi Arabia, South Africa, and Nigeria.", pinColor: "text-red-500", count: "" },
];

const whiteRoute = [
  "Direct carrier agreements with documented compliance",
  "Sender ID registration handled per country requirements",
  "Content policies aligned with local regulations",
  "Regular audits of route quality and delivery rates",
];

const routingLogic = [
  "Automatic route selection based on destination and message type",
  "Failover to backup routes when primary routes degrade",
  "Priority routing for time-sensitive messages like OTPs",
  "Cost-optimized routing for bulk promotional traffic",
];

const benefits = [
  { icon: FileCheck2, title: "Delivery Reports (DLRs)", desc: "Receive delivery confirmations via API webhook or pull from our reporting interface. DLRs include status, timestamps, and error codes when applicable.", color: "text-brand" },
  { icon: Gauge, title: "Route Performance", desc: "Monitor delivery rates, latency, and throughput per route and destination. Historical data available for trend analysis and capacity planning.", color: "text-sky" },
  { icon: BellRing, title: "Alerts & Notifications", desc: "Configure alerts for delivery rate drops, latency spikes, or route failures. Receive notifications via email, webhook, or dashboard.", color: "text-amber" },
];

const supportChannels = [
  "Email and ticketing system for standard requests",
  "Direct escalation path for urgent delivery issues",
  "Dedicated account management for enterprise clients",
];

const escalationProcess = [
  "Tier 1: Initial response and basic troubleshooting",
  "Tier 2: Route-level investigation and carrier coordination",
  "Tier 3: Engineering escalation for complex issues",
];

const faqs = [
  { question: "How many countries does Yootelco cover for SMS and voice delivery?", answer: "Yootelco provides SMS and voice coverage across major markets worldwide through direct carrier connections and vetted aggregator partnerships. Contact us for specific corridor availability and rates." },
  { question: "What is a white route and why is it important for SMS delivery?", answer: "A white route uses direct agreements with carriers and full compliance documentation. It guarantees maximum deliverability, prevents carrier filtering, and protects sender reputation, unlike grey routes which risk blocking and regulatory penalties." },
  { question: "Does Yootelco support SMS delivery in GDPR-regulated European markets?", answer: "Yes. Yootelco provides GDPR-compliant routing with alphanumeric sender ID registration across all EU member states and the UK, ensuring full regulatory compliance." },
  { question: "How does Yootelco handle SMS routing in emerging markets like Africa and Latin America?", answer: "Yootelco maintains direct routes to key markets including Brazil, Argentina, UAE, Saudi Arabia, South Africa, and Nigeria, with local sender ID registration support and premium routing for reliable delivery." },
  { question: "What delivery reports and analytics does Yootelco provide?", answer: "Yootelco provides real-time DLRs (delivery receipts) via API webhook or dashboard, including delivery status, timestamps, and error codes. Route performance data is available for trend analysis and capacity planning." },
  { question: "How does Yootelco ensure route quality and compliance across all markets?", answer: "All routes are vetted before activation. Yootelco performs regular audits, monitors delivery rates and latency in real time, and uses only white routes to maintain the highest quality and compliance standards." },
  { question: "What support does Yootelco offer for route issues or delivery failures?", answer: "Yootelco provides a 3-tier support structure, standard ticketing for general requests, direct escalation for urgent delivery issues, and engineering-level support for complex carrier coordination problems." },
  { question: "Can Yootelco handle high-volume SMS traffic across multiple regions simultaneously?", answer: "Yes. Yootelco's infrastructure is built for enterprise-scale throughput with automatic load balancing, redundant routing, and 99.99% uptime SLA across all covered regions." },
];

const RegionCard = ({ region, className }: { region: Region; className?: string }) => (
  <article className={`flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift ${className || ""}`}>
    <MapPin className={`h-14 w-14 ${region.pinColor}`} strokeWidth={2.2} fill="currentColor" fillOpacity={0.15} />
    <h3 className="mt-4 text-[17px] font-bold text-foreground">{region.title}</h3>
    <p className="mt-2 text-[13.5px] leading-[1.6] text-muted-foreground">{region.desc}</p>
  </article>
);

const BulletCard = ({ title, items, gradient }: { title: string; items: string[]; gradient: string }) => (
  <div className={`rounded-[18px] bg-gradient-to-br ${gradient} px-7 py-8 text-white shadow-soft md:px-9 md:py-10`}>
    <h3 className="text-[22px] font-bold md:text-[24px]">{title}</h3>
    <ul className="mt-5 space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-[14px] leading-[1.55] text-white/95">
          <Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={3} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CoverageRoutes = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const metaTitle = "Global SMS & Voice Coverage | Routes & Compliance | Yootelco";
  const metaDescription =
    "Yootelco provides direct carrier SMS and voice routes worldwide. White-route only policy, real-time delivery monitoring and full compliance support.";

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <main className="min-h-screen bg-white">
      <Seo title={metaTitle} description={metaDescription} path="/coverage-routes" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[900px] text-center">
            <h1 className="font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px]">
              Coverage & Routes
            </h1>
            <p className="mx-auto mt-[18px] max-w-[640px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px]">
              Direct carrier connections worldwide. White-route only policy, real-time delivery monitoring, and full compliance support for every market we cover.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Global Coverage Overview */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Global Coverage Overview</h2>
          <p className="mx-auto mt-4 max-w-[760px] text-[15px] leading-[1.6] text-muted-foreground">
            We maintain direct carrier relationships and vetted aggregator partnerships to deliver messages worldwide. Coverage spans major markets with direct routes and emerging markets through compliant partners.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1080px] grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-6">
          {regions.map((r, i) => (
            <RegionCard key={r.title} region={r} className={`md:col-span-2 ${i === 3 ? "md:col-start-2" : ""}`} />
          ))}
        </div>
      </section>

      {/* Route Quality & Compliance */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Route Quality & Compliance</h2>
          <p className="mx-auto mt-4 max-w-[760px] text-[15px] leading-[1.6] text-muted-foreground">
            All routes are vetted for compliance and quality before activation. We prioritize direct carrier connections and avoid grey routes that risk filtering or regulatory issues.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 md:grid-cols-2">
          <BulletCard title="White Route Policy" items={whiteRoute} gradient="from-violet-500 to-purple-600" />
          <BulletCard title="Routing Logic" items={routingLogic} gradient="from-blue-500 to-indigo-600" />
        </div>
      </section>

      {/* Key Benefits */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Key Benefits</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 md:grid-cols-3">
          {benefits.map((b) => {
            const Icon = b.icon;
            return (
              <article key={b.title} className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                <FlatIcon Icon={Icon} color={b.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{b.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{b.desc}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Support & Escalation */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Support & Escalation</h2>
          <p className="mx-auto mt-4 max-w-[760px] text-[15px] leading-[1.6] text-muted-foreground">
            Technical support available for route issues, delivery troubleshooting, and integration assistance.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 md:grid-cols-2">
          <BulletCard title="Support Channels" items={supportChannels} gradient="from-pink-500 to-rose-500" />
          <BulletCard title="Escalation Process" items={escalationProcess} gradient="from-fuchsia-500 to-violet-600" />
        </div>
      </section>

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="section-shell">
        <div className="cta-shell relative isolate overflow-hidden rounded-[36px] border border-border/70 px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <div className="cta-grid-overlay absolute inset-0" aria-hidden="true" />
          <CtaTopScribble className="absolute right-5 top-4 z-0 h-auto w-[108px] opacity-80 md:right-8 md:top-6 md:w-[126px]" />
          <div className="relative z-10">
            <h2 className="section-title">
              <span className="text-sky">Ready</span> to get started?
            </h2>
            <div className="mx-auto mt-5 flex max-w-2xl flex-col items-center">
              <p className="text-lg text-muted-foreground">Activate global routes with direct carrier coverage.</p>
              <CtaUnderlineScribble className="mt-2 h-auto w-[92px]" />
            </div>
            <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default CoverageRoutes;